import { useState } from 'react';
import pMap from 'p-map-browser';
import axios from 'axios';
import { useMutation } from 'react-apollo';

import { getErrorString } from '../util/normalize-errors';
import { photosUpload as photosUploadMutation } from '../graphql/mutations/photos';

const getUploadDataDefault = photoUploadsRes => {
  const [photoUploadData] = photoUploadsRes.data.photosUpload.photo_uploads;

  return {
    photoId: photoUploadData.photo_id,
    uploadUrl: photoUploadData.upload_url,
  };
};

/**
 * Hook to perform photo uploads.
 *
 * @param {Object} opts Options object.
 * @param {function} opts.getUploadData A method to - given the response from the uploadMutation of a single file - get the PhotoID and Upload URL.
 * @param {function} opts.onPhotoUploadSuccess Success callback.
 */
const usePhotosUpload = ({
  getUploadData = getUploadDataDefault,
  onPhotoUploadSuccess,
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState([]);
  const [photosUpload] = useMutation(photosUploadMutation);

  const onPhotoUpload = async ({ files, accountId, ...mutationOpts }) => {
    setUploading(true);
    setUploadedPhotos([]);

    const uploadResults = await pMap(files, async file => {
      const fileData = {
        filename: file.name,
        content_type: file.type,
      };

      try {
        const photoUploadsRes = await photosUpload({
          variables: {
            input: {
              account_id: accountId,
              files: [fileData],
              ...mutationOpts,
            },
          },
        });

        const { uploadUrl, photoId } = getUploadData(photoUploadsRes);

        await axios.put(uploadUrl, file, {
          headers: {
            'Content-Type': file.type,
          },
        });

        const data = {
          file,
          photoId,
        };

        if (onPhotoUploadSuccess) {
          onPhotoUploadSuccess(data);
        }

        return data;
      } catch (error) {
        return {
          file,
          error: getErrorString(error),
        };
      }
    });

    setUploading(false);
    setUploadedPhotos(uploadResults);

    return uploadResults;
  };

  return {
    onPhotoUpload,
    uploading,
    uploadedPhotos,
  };
};

export default usePhotosUpload;
